import React from "react";
import { useNavigate } from "react-router-dom";
import { useClerk } from "@clerk/clerk-react";
import { 
  LayoutDashboard, 
  Users, 
  Briefcase, 
  BookOpen, 
  Layers, 
  Sparkles, 
  FolderOpen, 
  Settings,
  CreditCard,
  ChevronLeft,
  ChevronRight,
  LogOut,
  ArrowLeft,
  X
} from "lucide-react";
import type { OrgStats, WorkspaceItem, InvitationItem, MemberRow, OrgDocument } from "../types";

export type TabId = 
  | "overview"
  | "members"
  | "workspaces"
  | "courses"
  | "assignments"
  | "intelligence"
  | "documents"
  | "billing"
  | "settings";

interface OrgSidebarProps {
  activeTab: TabId;
  onSelectTab: (tab: TabId) => void;
  stats: OrgStats | null;
  workspaces: WorkspaceItem[];
  members: MemberRow[];
  invitations: InvitationItem[];
  documents: OrgDocument[];
  creditsSummary?: { ai_credits_balance?: number } | null;
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  isMobileOpen: boolean;
  onCloseMobile: () => void;
}

interface NavGroup {
  title: string;
  items: {
    id: TabId;
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    badge?: number | string | null;
    badgeColor?: string;
  }[];
}

export const OrgSidebar: React.FC<OrgSidebarProps> = ({
  activeTab,
  onSelectTab,
  stats,
  workspaces,
  members,
  invitations,
  documents,
  creditsSummary,
  isCollapsed,
  onToggleCollapse,
  isMobileOpen,
  onCloseMobile,
}) => {
  const navigate = useNavigate();
  const { signOut } = useClerk();

  const handleSignOut = () => {
    void signOut().then(() => {
      navigate("/sign-in");
    });
  };

  const pendingInvitesCount = invitations.filter((inv) => inv.status === "pending").length;
  const totalRoster =
    members.length > 0 || invitations.length > 0
      ? members.length + invitations.length
      : stats?.total_members || 0;

  const navGroups: NavGroup[] = [
    {
      title: "Overview",
      items: [
        {
          id: "overview",
          label: "Dashboard Home",
          icon: LayoutDashboard,
        },
      ],
    },
    {
      title: "Team & Learning",
      items: [
        {
          id: "members",
          label: "Team Members",
          icon: Users,
          badge: pendingInvitesCount > 0 ? `${totalRoster} (+${pendingInvitesCount})` : totalRoster > 0 ? totalRoster : null,
          badgeColor: pendingInvitesCount > 0 ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-700",
        },
        {
          id: "workspaces",
          label: "Workspaces",
          icon: Briefcase,
          badge: workspaces.length > 0 ? workspaces.length : null,
          badgeColor: "bg-indigo-100 text-indigo-700",
        },
        {
          id: "courses",
          label: "Sponsored Courses",
          icon: BookOpen,
          badge: stats?.sponsored_courses?.length ? stats.sponsored_courses.length : null,
          badgeColor: "bg-blue-100 text-blue-700",
        },
        {
          id: "assignments",
          label: "Learning Rules",
          icon: Layers,
        },
      ],
    },
    {
      title: "AI & Knowledge",
      items: [
        {
          id: "intelligence",
          label: "Skill Intelligence",
          icon: Sparkles,
        },
        {
          id: "documents",
          label: "Knowledge & SOPs",
          icon: FolderOpen,
          badge: documents.length > 0 ? documents.length : null,
          badgeColor: "bg-emerald-100 text-emerald-700",
        },
      ],
    },
    {
      title: "Admin",
      items: [
        {
          id: "billing",
          label: "Billing & Credits",
          icon: CreditCard,
          badge: creditsSummary ? `${creditsSummary.ai_credits_balance ?? 0} cr` : null,
          badgeColor: "bg-indigo-50 text-indigo-700 border border-indigo-200",
        },
        {
          id: "settings",
          label: "Settings & Quota",
          icon: Settings,
        },
      ],
    },
  ];

  const handleItemClick = (id: TabId) => {
    onSelectTab(id);
    onCloseMobile();
  };

  const renderContent = () => (
    <div className="flex flex-col h-full justify-between">
      <div className="space-y-6">
        {navGroups.map((group, groupIdx) => (
          <div key={groupIdx} className="space-y-1">
            {!isCollapsed && (
              <div className="px-3 text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 select-none">
                {group.title}
              </div>
            )}
            <div className="space-y-1">
              {group.items.map((item) => {
                const Icon = item.icon;
                const isActive = activeTab === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => handleItemClick(item.id)}
                    title={isCollapsed ? item.label : undefined}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all duration-150 cursor-pointer ${
                      isActive
                        ? "bg-indigo-600 text-white shadow-xs font-bold"
                        : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                    } ${isCollapsed ? "justify-center px-2" : "justify-between"}`}
                  >
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-white" : "text-slate-500"}`} />
                      {!isCollapsed && <span className="truncate">{item.label}</span>}
                    </div>

                    {!isCollapsed && item.badge !== undefined && item.badge !== null && (
                      <span
                        className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md flex-shrink-0 ${
                          isActive ? "bg-white/20 text-white" : item.badgeColor || "bg-slate-100 text-slate-700"
                        }`}
                      >
                        {item.badge}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {/* Mobile Actions */}
      <div className="pt-4 border-t border-slate-200 mt-auto space-y-1">
        <button
          onClick={() => {
            navigate("/courses");
            onCloseMobile();
          }}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold text-slate-700 hover:bg-slate-100 transition cursor-pointer"
        >
          <ArrowLeft className="w-4 h-4 flex-shrink-0 text-slate-500" />
          <span>Back to Courses</span>
        </button>
        <button
          onClick={handleSignOut}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold text-rose-600 hover:bg-rose-50 transition cursor-pointer"
        >
          <LogOut className="w-4 h-4 flex-shrink-0 text-rose-500" />
          <span>Sign Out</span>
        </button>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile Drawer Overlay */}
      {isMobileOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs z-40 lg:hidden transition-opacity"
          onClick={onCloseMobile}
        />
      )}

      {/* Mobile Drawer */}
      <aside
        className={`fixed top-0 left-0 bottom-0 w-72 bg-white z-50 p-4 border-r border-slate-200 flex flex-col transition-transform duration-300 ease-in-out lg:hidden ${
          isMobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between pb-4 mb-4 border-b border-slate-200">
          <span className="font-bold text-sm text-slate-900">Dashboard Navigation</span>
          <button
            onClick={onCloseMobile}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg cursor-pointer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">{renderContent()}</div>
      </aside>

      {/* Desktop Fixed Sidebar */}
      <aside
        className={`hidden lg:flex flex-col fixed left-0 top-20 h-[calc(100dvh-5rem)] p-4 bg-white/80 backdrop-blur-md border-r border-slate-200 transition-all duration-300 flex-shrink-0 z-20 ${
          isCollapsed ? "w-20" : "w-64"
        }`}
      >
        <div className="flex-1 overflow-y-auto pr-1 -mr-1">
          <div className="space-y-6">
            {navGroups.map((group, groupIdx) => (
              <div key={groupIdx} className="space-y-1">
                {!isCollapsed && (
                  <div className="px-3 text-[11px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 select-none">
                    {group.title}
                  </div>
                )}
                <div className="space-y-1">
                  {group.items.map((item) => {
                    const Icon = item.icon;
                    const isActive = activeTab === item.id;
                    return (
                      <button
                        key={item.id}
                        onClick={() => handleItemClick(item.id)}
                        title={isCollapsed ? item.label : undefined}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-xs font-semibold transition-all duration-150 cursor-pointer ${
                          isActive
                            ? "bg-indigo-600 text-white shadow-xs font-bold"
                            : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                        } ${isCollapsed ? "justify-center px-2" : "justify-between"}`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0">
                          <Icon className={`w-4 h-4 flex-shrink-0 ${isActive ? "text-white" : "text-slate-500"}`} />
                          {!isCollapsed && <span className="truncate">{item.label}</span>}
                        </div>

                        {!isCollapsed && item.badge !== undefined && item.badge !== null && (
                          <span
                            className={`text-[10px] font-bold px-1.5 py-0.5 rounded-md flex-shrink-0 ${
                              isActive ? "bg-white/20 text-white" : item.badgeColor || "bg-slate-100 text-slate-700"
                            }`}
                          >
                            {item.badge}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Action buttons at bottom for desktop */}
        <div className="hidden lg:block pt-3 mt-3 border-t border-slate-200 shrink-0 space-y-1">
          <button
            onClick={() => navigate("/courses")}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition cursor-pointer ${
              isCollapsed ? "justify-center px-2" : "justify-start"
            }`}
            title={isCollapsed ? "Back to Courses" : undefined}
          >
            <ArrowLeft className="w-4 h-4 flex-shrink-0 text-slate-500" />
            {!isCollapsed && <span>Back to Courses</span>}
          </button>
          <button
            onClick={handleSignOut}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-slate-600 hover:bg-rose-50 hover:text-rose-600 transition cursor-pointer ${
              isCollapsed ? "justify-center px-2" : "justify-start"
            }`}
            title={isCollapsed ? "Sign Out" : undefined}
          >
            <LogOut className="w-4 h-4 flex-shrink-0 text-rose-500" />
            {!isCollapsed && <span>Sign Out</span>}
          </button>
          <button
            onClick={onToggleCollapse}
            className={`w-full flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold text-slate-500 hover:bg-slate-100 hover:text-slate-900 transition cursor-pointer ${
              isCollapsed ? "justify-center px-2" : "justify-start"
            }`}
            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
          >
            {isCollapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <>
                <ChevronLeft className="w-4 h-4" />
                <span>Collapse Menu</span>
              </>
            )}
          </button>
        </div>
      </aside>
    </>
  );
};
